import * as _ from 'lodash';
import { useCallback, useEffect, useMemo, useState } from 'react';
import type { VoidFunctionComponent } from 'react';
import { Alert } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';

import useAsyncStatus from '../../../hooks/useAsyncStatus';
import useOnMount from '../../../hooks/useOnMount';
import useToggle from '../../../hooks/useToggle';
import { put } from '../../../redux/api';
import CWEStore from '../../../redux/cwe';
import PolicyStore from '../../../redux/policies';
import Button from '../../generic/Button';
import Loader from '../../generic/Loader';
import NotFound from '../../generic/NotFound';
import Text from '../../generic/Text';
import EditPolicyCTA from '../EditPolicyCTA';
import CVSSFilterComponent from './CVSSFilterComponent';
import type { AsyncStatus, CWEDescription, RouterProps } from '../../../types';
import type { ReduxStore } from '../../../redux/store';
import type { CVSSRule } from './types';
import type { SecurityPolicy } from '../../../../typings/api';
import type { FailedFetchResponse } from '../../../helpers/errors';
import ItemizedRuleList from './ItemizedRuleList';
import * as api from '../../../redux/api';
import CVEFilterComponent from './CVEFilterComponent';
import type { SecurityFilterType, SecurityRule, SecurityRuleType } from '../../../../shared/model-typings';
import { useIsSecurityEnabled } from '../../../helpers/features';
import Helmet from 'react-helmet';

type DisplaySecurityPolicyPageProps = RouterProps<{
  policyId: string;
}>;

type DisplayQualityPolicyPageMainContentsProps = {
  policy: SecurityPolicy;
  status: AsyncStatus;
};

const DEFAULT_CVSS_RULE: CVSSRule = {
  ruleId: null,
  cvssFilter: 'LEVEL',
  minimumCVSSLevel: 'LOW',
  minimumCVSSValue: 0,
};

type ItemizedListData = Array<string | null>;

const DisplaySecurityPolicyPageContent: VoidFunctionComponent<DisplayQualityPolicyPageMainContentsProps> = ({
  status,
  policy,
}) => {
  const dispatch = useDispatch();
  const cwes = useSelector(CWEStore.selectors.getCweList);
  const [cvssRule, setCvssRule] = useState<CVSSRule>(DEFAULT_CVSS_RULE);
  const [hasChanged, setHasChanged] = useState(false);
  const [isShowingSuccess, onToggleShowingSuccess] = useToggle();
  const [cweAllowList, setCweAllowListOg] = useState<ItemizedListData>([]);
  const [cweDenyList, setCweDenyListOg] = useState<ItemizedListData>([]);
  const [cveAllowList, setCveAllowListOg] = useState<ItemizedListData>([]);

  const setListAndHasChanged = (setter: (v: ItemizedListData) => void, currentList: ItemizedListData) =>
    useCallback((newList: ItemizedListData) => {
      const forwardDiff = _.difference(currentList, newList);
      const backwardsDiff = _.difference(newList, currentList);
      if (forwardDiff.length > 0 || backwardsDiff.length > 0) {
        // null is a signal for a new editable entry, so nothing will have changed
        const doesForwardDiffHaveNullOnly = (forwardDiff.length === 1 && forwardDiff[0] === null);
        const doesBackwardDiffHaveNullOnly = (backwardsDiff.length === 1 && backwardsDiff[0] === null);
        if (!doesForwardDiffHaveNullOnly && !doesBackwardDiffHaveNullOnly) {
          setHasChanged(true);
        }

        setter(newList);
      }
    }, [currentList, setter]);

  const setCweAllowList = setListAndHasChanged(setCweAllowListOg, cweAllowList);
  const setCweDenyList = setListAndHasChanged(setCweDenyListOg, cweDenyList);
  const setCveAllowList = setListAndHasChanged(setCveAllowListOg, cveAllowList);

  const [areDisputedCVEsIgnored, setIgnoreDisputedCVEs] = useState(false);

  // The updateRules Redux action doesn't handle errors and doesn't really need to be redux, so using this method.
  const { status: submitStatus, onPending, onSuccess, onError } = useAsyncStatus();

  // componentsWillReceiveProps equivalent
  useEffect(() => {
    const newCweAllowlist: string[] = [];
    const newCweDenylist: string[] = [];
    let newCveAllowList: string[] = [];

    policy.rules?.forEach((rule) => {
      const cweRule: number = (rule.whitelistedCWE || rule.blacklistedCWE) as number;

      if (rule.type === 'vulnerability') {
        if (rule.allowedCves?.length) {
          newCveAllowList = _.uniq([...newCveAllowList, ...rule.allowedCves]);
        } else if (rule.filterType === 'WHITELIST') {
          newCweAllowlist.push(cweRule.toString());
        } else if (rule.filterType === 'BLACKLIST') {
          newCweDenylist.push(cweRule.toString());
        } else if (rule.filterType === 'FILTER') {
          setCvssRule({
            ruleId: rule.ruleId,
            cvssFilter: rule.cvssFilter || 'LEVEL',
            minimumCVSSLevel: rule.minimumCVSSLevel || 'LOW',
            minimumCVSSValue: rule.minimumCVSSValue || 0,
          });
        } else if (rule.filterType === 'CVE_STATUS') {
          setIgnoreDisputedCVEs(true);
        }
      }
    });

    setCweAllowList(newCweAllowlist);
    setCweDenyList(newCweDenylist);
    setCveAllowList(newCveAllowList);
    // We don't want to include the toggle functions because they will cause this effect to loop infinitely
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [policy, dispatch, status]);

  /**
   * Check to see if the given CWE is in the valid CWE list
   */
  const isValidCWE = useCallback((cwe: string): boolean => {
    const cweId = Number(cwe);
    return !Number.isNaN(cweId) &&
      Number.isInteger(cweId)
      && cwes.some((cwd: CWEDescription) => cwd.id === cweId);
  }, [cwes]);

  /**
   * Helper function for submission to keep dependency arrays somewhat manageable.
   */
  const submitRules = useCallback(async (policyId: number, rules: Array<Partial<SecurityRule>>) => {
    onPending();

    try {
      await put(`/api/policies/${policyId}/rules`, rules);
      onSuccess();
      setHasChanged(false);
      onToggleShowingSuccess();
    } catch (err) {
      onError(err as FailedFetchResponse);
    }
  }, [onError, onPending, onSuccess, setHasChanged, onToggleShowingSuccess]);

  /**
   * Function for saving the updated policy rules
   */
  const onSavePolicy = useCallback(async () => {
    const cvssRuleToAdd: Partial<SecurityRule> = {
      type: 'vulnerability' as SecurityRuleType,
      filterType: 'FILTER' as SecurityFilterType,
      minimumCVSSLevel:
        cvssRule.cvssFilter === 'LEVEL' ? cvssRule.minimumCVSSLevel : null,
      minimumCVSSValue:
        cvssRule.cvssFilter === 'VALUE' ? cvssRule.minimumCVSSValue : null,
      cvssFilter: cvssRule.cvssFilter,
    };

    const allowedCvesRule: Partial<SecurityRule> = {
      type: 'vulnerability',
      allowedCves: cveAllowList.filter(_.identity) as string[],
    };

    const deniedCWERules = cweDenyList
      .filter(isValidCWE) // Remove non-valid cwe values
      .map((rule) => ({
        type: 'vulnerability' as SecurityRuleType,
        filterType: 'BLACKLIST' as SecurityFilterType,
        blacklistedCWE: Number(rule),
      }));

    const allowedCWERules = cweAllowList
      .filter(isValidCWE) // Remove non-valid cwe values
      .map((rule) => ({
        type: 'vulnerability' as SecurityRuleType,
        filterType: 'WHITELIST' as SecurityFilterType,
        whitelistedCWE: Number(rule),
      }));

    const aggregatedRules: Array<Partial<SecurityRule>> = [
      ...deniedCWERules,
      ...allowedCWERules,
      cvssRuleToAdd,
    ];

    if (allowedCvesRule.allowedCves?.length) {
      aggregatedRules.push(allowedCvesRule);
    }

    if (areDisputedCVEsIgnored) {
      aggregatedRules.push({
        type: 'vulnerability' as SecurityRuleType,
        filterType: 'CVE_STATUS' as SecurityFilterType,
      });
    }

    await submitRules(policy.id, aggregatedRules);
  }, [
    cvssRule,
    isValidCWE,
    cweAllowList,
    cweDenyList,
    cveAllowList,
    policy,
    submitRules,
    areDisputedCVEsIgnored,
  ]);

  const allErrors = useMemo(() => {
    return _.compact(
      _.flatten([
        ...[status.error],
        ...(status.errors || []),
        ...[submitStatus.error],
        ...(submitStatus.errors || []),
      ]),
    );
  }, [status, submitStatus]);

  const cwesList = useSelector(CWEStore.selectors.getCweList);

  const getCweTitle = (id: string) => cwesList.find((cwe) => `${cwe.id}` === `${id}`)?.title || id;
  // the getSuggestions callback requires a promise be returned. Marking this as async fulfils the contract
  const getCweSuggestions = async (cwe: string) => {
    // only return cwes with related details
    return cwesList.filter(({ id, title }) => `${id}`.includes(cwe) || title.toLowerCase().includes(cwe.toLowerCase()))
    // limit # of results returned
      .slice(0, 5);
  };
  const buildCweLink = (id: string) => `https://cwe.mitre.org/data/definitions/${id}.html`;

  const buildCveLink = (cve: string) => `https://nvd.nist.gov/vuln/detail/${cve}`;
  const getCveSuggestions = async (cve: string) => {
    const { data } = await api.get<Array<{ cve: string, description?: string }>>('/api/vulns/cve-list', true, {
      query: cve,
    });

    return data?.map((suggestion) => ({
      link: buildCveLink(cve),
      description: suggestion.description,
      id: suggestion.cve,
    }));
  };
  const getCveTitle = () => undefined;

  return (
    <>
      <EditPolicyCTA policy={policy} status={status} />
      <div className="margin-top-lg security-policy-container">
        <Helmet title={status.isLoading ? 'Policy...' : `${policy.title} - Policies`} />

        <div className="container card">

          {isShowingSuccess && (
            <Alert bsStyle="success" onDismiss={onToggleShowingSuccess} >
              Security rule(s) successfully saved!
            </Alert>
          )}

          {allErrors.length > 0 && allErrors.map((err) => (
            <div key={err} className="alert alert-error">{err}</div>
          ))}

          <div className="header-container">
            {/* Group elements for flex container */}
            <div>
              <Text.H1>Security Policy</Text.H1>
              <p>Manage how security issues are assigned to projects with this policy</p>
            </div>
            <Button
              type="primary"
              size="large"
              onClick={onSavePolicy}
              isDisabled={status.isLoading || !hasChanged}
            > Save
            </Button>
          </div>

          {status.loading
            ? <Loader />
            : (
              <div>
                <div className="padding-left-md">
                  <Text.H2>Vulnerability Rules</Text.H2>
                </div>
                <CVSSFilterComponent
                  cvssRule={cvssRule}
                  setCvssRule={setCvssRule}
                  setChanged={setHasChanged}
                />
                <ItemizedRuleList
                  listItemInputDecoration="CWE-"
                  buildLink={buildCweLink}
                  setRuleList={setCweAllowList}
                  getTitle={getCweTitle}
                  rules={cweAllowList}
                  listTitle="CWE Allow Rules"
                  listDescription={
                    <p>
                      Vulnerabilities with CWEs defined in these rules will <b>never</b> flag an security issue.
                    </p>
                  }
                  getSuggestions={getCweSuggestions}
                />
                <ItemizedRuleList
                  listItemInputDecoration="CWE-"
                  buildLink={buildCweLink}
                  setRuleList={setCweDenyList}
                  getTitle={getCweTitle}
                  rules={cweDenyList}
                  listTitle="CWE Deny Rules"
                  listDescription={
                    <p>
                      Vulnerabilities with CWEs defined in these rules will <b>always</b> flag a security issue
                    </p>
                  }
                  getSuggestions={getCweSuggestions}
                />
                <ItemizedRuleList
                  listItemInputDecoration="CVE-"
                  buildLink={buildCveLink}
                  setRuleList={setCveAllowList}
                  getTitle={getCveTitle}
                  rules={cveAllowList}
                  listTitle="CVE Allow Rules"
                  listDescription={
                    <p>
                      Vulnerabilities with these CVEs will <b>never</b> flag a security issue
                    </p>
                  }
                  getSuggestions={getCveSuggestions}
                />
                <div className="padding-left-md" style={{ marginTop: '50px' }}>
                  <Text.H2>CVE Filter Rules</Text.H2>
                </div>
                <CVEFilterComponent
                  title="Show disputed CVEs"
                  description="Show disputed CVEs in views and reports."
                  isEnabled={areDisputedCVEsIgnored}
                  onToggle={() => {
                    setHasChanged(true);
                    setIgnoreDisputedCVEs(!areDisputedCVEsIgnored);
                  }}
                />
              </div>
              )}
        </div>
      </div>
    </>
  );
};

const DisplaySecurityPolicyPage: VoidFunctionComponent<DisplaySecurityPolicyPageProps> = (props) => {
  const dispatch = useDispatch();
  const policy = useSelector((state: ReduxStore) => state.policies.policies[props.params.policyId]);
  const status = useSelector(PolicyStore.selectors.getStatus);

  const hasSecurity = useIsSecurityEnabled();

  useOnMount(() => {
    dispatch(PolicyStore.actions.fetchOne(Number(props.params.policyId)));
    dispatch(CWEStore.actions.fetchCweList());
  });

  if (!hasSecurity || _.isNil(props.params.policyId)) return <NotFound />;
  if (!policy) return <Loader />;

  return (
    <DisplaySecurityPolicyPageContent
      policy={policy}
      status={status}
    />
  );
};

export default DisplaySecurityPolicyPage;
